import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * Regents Park, on the day: a reminder that tonight is the last event of
 * the visit in Sydney, to Sydney people who haven't already been invited
 * to it. The backend works out who that is.
 */
export function RegentsParkTodayPanel({
  defaultTestEmail,
  senderName,
  alreadySentNote,
}: {
  defaultTestEmail: string;
  senderName: string | null;
  alreadySentNote: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const send = trpc.adminSendRegentsParkToday.useMutation();
  const count = trpc.adminSendRegentsParkToday.useMutation();

  const run = (mode: "test" | "all") => {
    send.mutate(
      {
        mode,
        testEmail: mode === "test" ? flow.testAddress.trim() : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped
                ? EMAIL_OFF_SENTENCE
                : `Test sent to ${res.sentTo}. Read it through, then send.`,
            );
            count.mutate({ mode: "dry-run" });
          } else if (res.mode === "all") {
            flow.sendAllDone(
              sendAllSentence(res, "There is no one left to send this to."),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  const recipients =
    count.data?.mode === "dry-run" ? count.data.recipients : null;

  return (
    <>
      <p className={BODY_TEXT}>
        Tells people tonight is the last event of this visit in Sydney. Goes to
        everyone who has booked a Sydney event with us, except people already
        registered for Regents Park, people checked in yesterday at Castle Hill
        (their thank-you invited them), the community organisations and
        addresses that have bounced.
        {recipients === null
          ? " Send yourself a test to see how many people that is."
          : ` That is ${people(recipients)}.`}
      </p>
      <TestThenSend
        flow={flow}
        emailName="Regents Park tonight"
        audience={
          recipients === null ? "everyone not yet invited" : people(recipients)
        }
        noAudienceText=""
        busy={send.isPending}
        onSendTest={() => run("test")}
        onSendAll={() => run("all")}
        alreadySentNote={alreadySentNote}
      />
    </>
  );
}
